import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import saveAs from "file-saver";
import { FC, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useDownloadSmartTemplate } from "../../../../api/services/bulkCreateProgram/bulkCreateProgramApis";
import { useSearchParticipantListMutation } from "../../../../api/services/bulkInsertActivity/bulkInsertApis";
import { ReactComponent as ResetAllIcon } from "../../../../assets/images/reset-all.svg";
import {
  AccordionHeader,
  AccordionHeaderText,
  BulkInsertFooter,
  InformationModal,
  MuiBox,
  MuiButtonPrimary,
  MuiTypography,
} from "../../../../components";
import { ButtonWithIcon } from "../../../../components/buttonWithIcon/ButtonWithIcon";
import { BE_ERROR_CODES, DATE_FORMAT } from "../../../../helpers/constants";
import useAccordion from "../../../../hooks/useAccordion";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { useToast } from "../../../../hooks/useToast";
import { toggleLoader } from "../../../../redux/slices/appState/appStateSlice";
import { Separator } from "../../../../styles/common.style";
import { SearchParticipantResult } from "../../../createBulkActivity/modelsAndSchemas/Models";
import {
  bulkEditBaseFileName,
  BulkEditSearchParticipantFormType,
  defaultFilter,
  ModalState,
  ModalType,
  searchParticipantsDefaultValues,
} from "../../modelsAndSchema/Model";
import { BulkEditProgramSearchParticipantSchema } from "../../modelsAndSchema/ValidationSchema";
import {
  bulkEditHeaderFooterSiteCoreKeys,
  bulkEditProgramSearchParticipantKeys,
  bulkEditSearchParticipantResultsSiteCoreKeys,
} from "../../siteCore/siteCoreKeys";
import { BulkProgramSearchParticipantsWrapper } from "./BulkEditSearchParticipant.style";
import { SearchParticipantsForm } from "./searchParticipantsForm/SearchParticipantsForm";
import { SearchParticipantsResults } from "./searchParticipantsResults/SearchParticipantsResults";
import { isErrorCode, isNotEmpty } from "../../../../helpers/utils";

interface BulkEditSearchParticipantProps {
  pageId: string;
  setEditCurrentStep: (step: number) => void;
}

const initialAccordionStatus = {
  searchParticipant: true,
  searchResult: true,
  all: true,
};

export const BulkEditSearchParticipant: FC<BulkEditSearchParticipantProps> = ({
  pageId,
  setEditCurrentStep,
}) => {
  const [informationModalState, setInformationModalState] =
    useState<ModalState>({ type: null, show: false });
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [selectedParticipants, setSelectedParticipants] = useState<
    SearchParticipantResult[]
  >([]);

  const deliveryCountryRef = useRef<{
    resetValues: (values?: string[]) => void;
  }>(null);
  const productLineRef = useRef<{ resetValues: (values?: string[]) => void }>(
    null,
  );
  const {
    commonValidationMessages,
    commonLabels,
    fetchComponentTitle,
    fetchLabel,
  } = useSiteCore();
  const { displayToast } = useToast();
  const dispatch = useDispatch();
  const [isSearchClick, setIsSearchClick] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const { accordionStatus, handleIndividualAccordionChange } = useAccordion(
    initialAccordionStatus,
  );

  const methods = useForm<BulkEditSearchParticipantFormType>({
    mode: "all",
    defaultValues: searchParticipantsDefaultValues,
    resolver: yupResolver(
      BulkEditProgramSearchParticipantSchema(commonValidationMessages),
    ),
  });

  const {
    handleSubmit,
    reset,
    getValues,
    formState: { isDirty },
  } = methods;

  const {
    mutateAsync: getParticipantList,
    isPending: isParticipantPending,
    isError: isParticipantError,
    data: participantResults,
    error: searchParticipantError,
  } = useSearchParticipantListMutation();

  const {
    data: smartTemplateData,
    isPending: smartTemplatePending,
    isError: smartTemplateError,
    isSuccess: smartTemplateSuccess,
    mutateAsync: refetchSmartTemplate,
  } = useDownloadSmartTemplate();

  useEffect(() => {
    if (smartTemplateData) {
      if (smartTemplateData instanceof Blob) {
        saveAs(
          smartTemplateData,
          `${bulkEditBaseFileName}${dayjs().format(DATE_FORMAT.DD_MMM_YYYY_HHMMSS_UNDERSCORE)}.xlsx`,
        );
      }
    }
  }, [smartTemplateData]);

  useEffect(() => {
    if (smartTemplateSuccess) {
      handleNavigateBack();
    }
  }, [smartTemplateSuccess]);

  const handleCloseErrorMessage = () => {
    setShowErrorMessage(false);
  };

  const handleResetButton = () => {
    reset(searchParticipantsDefaultValues);
    setIsSearchClick(false);
    setSelectedClientId("");
    deliveryCountryRef.current?.resetValues();
    productLineRef.current?.resetValues();
    getParticipantListApi();
  };

  const handleSearchClick = () => {
    getParticipantListApi();
    setIsSearchClick(true);
    setSelectedClientId(getValues("clientId") ?? "");
  };

  const prepareData = () => {
    const {
      clientName,
      productLine,
      programTemplate,
      participantStatus,
      deliveryCountry,
    } = getValues();

    return {
      client: clientName,
      clientFilter: defaultFilter,
      lob: productLine,
      programTemplateId: isNotEmpty(programTemplate) ? [programTemplate] : [],
      status: participantStatus,
      deliveryCountryCode: deliveryCountry,
    };
  };

  const getParticipantListApi = () => {
    getParticipantList(prepareData());
  };

  useEffect(() => {
    if(isParticipantError || searchParticipantError) {
      if (
        isErrorCode(
          searchParticipantError,
          BE_ERROR_CODES.SEARCH_PROGRAM_LIMIT_EXCEEDED,
        )
      ) {
        setShowErrorMessage(true);
        setIsSearchClick(false);
      } else {
        displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      }
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isParticipantError, searchParticipantError]);

  useEffect(() => {
    if(smartTemplateError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [smartTemplateError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isParticipantPending: {
          isVisible: isParticipantPending || smartTemplatePending,
        },
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          isParticipantPending: {
            isVisible: false,
          },
        }),
      );
    };
  }, [isParticipantPending, smartTemplatePending]);

  const renderResultsHeader = (title: string, selectedCount: number) => {
    return (
      <MuiBox className="form-header">
        <MuiBox className="form-header-left">
          <MuiBox className="title-container">
            <MuiTypography
              variant="h2SemiBold"
              className="form-header-left"
              component="h2"
            >
              {title}
            </MuiTypography>
            {selectedCount > 0 && (
              <MuiTypography variant="h3Bold" className="selected-count">
                {selectedCount}
              </MuiTypography>
            )}
          </MuiBox>
        </MuiBox>
      </MuiBox>
    );
  };

  const handleCloseInformation = () => {
    setInformationModalState({
      type: null,
      show: false,
    });
  };

  const onDownloadButtonClick = () => {
    // if less than 2 selected participants, show information modal
    if (selectedParticipants.filter((p) => p.isSelected).length < 2) {
      setInformationModalState({
        type: ModalType.MinimumParticipantsRequired,
        show: true,
      });
    } else {
      // download smart template API call
      const { clientId } = getValues();
      const programId = selectedParticipants
        .filter((participant) => participant.isSelected)
        .map((participant) => participant.programId);

      refetchSmartTemplate({ clientId, programId });
    }
  };

  const handleNavigateBack = () => {
    setEditCurrentStep(0);
  };

  const getModalMessage = (type: ModalType | null): string => {
    switch (type) {
      case ModalType.ClientValidation:
        return fetchLabel(
          bulkEditSearchParticipantResultsSiteCoreKeys.participantsResultsKey,
          bulkEditSearchParticipantResultsSiteCoreKeys.selectClientToCountinueMessage,
        );
      case ModalType.MinimumParticipantsRequired:
        return fetchLabel(
          bulkEditSearchParticipantResultsSiteCoreKeys.participantsResultsKey,
          bulkEditSearchParticipantResultsSiteCoreKeys.selectAtLeastTwoParticipantsMessage,
        );
      default:
        return "";
    }
  };

  return (
    <BulkProgramSearchParticipantsWrapper>
      <MuiBox className="accordion-header" data-testid="search-participant">
        <FormProvider {...methods}>
          <AccordionHeader
            title={
              <AccordionHeaderText
                label={fetchComponentTitle(
                  bulkEditProgramSearchParticipantKeys.searchParticipantsKey,
                )}
              />
            }
            onChange={() =>
              handleIndividualAccordionChange("searchParticipant")
            }
            isExpanded={accordionStatus.searchParticipant}
          >
            <MuiBox className="semi-header">
              <ButtonWithIcon
                data-testid={`${pageId}-clearAll`}
                buttonText={commonLabels.resetAllSearch}
                onActionClick={() => handleResetButton()}
                ActionIcon={ResetAllIcon}
              />
            </MuiBox>
            <Separator />
            <SearchParticipantsForm
              pageId={pageId}
              deliveryCountryRef={deliveryCountryRef}
              productLineRef={productLineRef}
            />
            <MuiBox className="search-button-container">
              <MuiButtonPrimary
                id={`${pageId}-search`}
                onClick={() => handleSubmit(handleSearchClick)()}
              >
                {commonLabels.search}
              </MuiButtonPrimary>
            </MuiBox>
          </AccordionHeader>
        </FormProvider>
      </MuiBox>

      <MuiBox className="accordion-header">
        <AccordionHeader
          title={renderResultsHeader(
            isSearchClick
              ? fetchComponentTitle(
                  bulkEditSearchParticipantResultsSiteCoreKeys.participantsResultsKey,
                )
              : fetchLabel(
                  bulkEditSearchParticipantResultsSiteCoreKeys.participantsResultsKey,
                  bulkEditSearchParticipantResultsSiteCoreKeys.allParticipantsSectionTitle,
                ),
            participantResults?.data?.length ?? 0,
          )}
          onChange={() => handleIndividualAccordionChange("searchResult")}
          isExpanded={accordionStatus.searchResult}
        >
          <SearchParticipantsResults
            rows={participantResults?.data ?? []}
            pageId={pageId}
            setShowInformationModal={setInformationModalState}
            selectedClientId={selectedClientId}
            setSelectedParticipants={setSelectedParticipants}
          />
        </AccordionHeader>
      </MuiBox>
      <BulkInsertFooter
        pageId={pageId}
        onConfirmClick={onDownloadButtonClick}
        handleNavigateBack={handleNavigateBack}
        isFiledUpdated={isDirty}
        confirmButtonText={fetchLabel(
          bulkEditHeaderFooterSiteCoreKeys.headerAndFooterComponent,
          bulkEditHeaderFooterSiteCoreKeys.downloadSmartTemplate,
        )}
      />

      {informationModalState.show && (
        <InformationModal
          showModal={informationModalState.show}
          handleModalClose={handleCloseInformation}
          handlePrimaryButtonClick={handleCloseInformation}
          message={getModalMessage(informationModalState.type)}
        />
      )}

      {showErrorMessage && (
        <InformationModal
          showModal={showErrorMessage}
          handleModalClose={handleCloseErrorMessage}
          handlePrimaryButtonClick={handleCloseErrorMessage}
          message={commonLabels.refineYourSearchInformationMessage}
        />
      )}
    </BulkProgramSearchParticipantsWrapper>
  );
};
